import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { Segment } from '../../shared/types/segment.interface';

/**
 * Modal dialog component used to confirm and trigger deletion of a Segment within a campaign.
 * @component
 * @selector app-delete-segment-modal
 * @example
 *   <app-delete-segment-modal
 *     [visible]="true"
 *     [segment]="selectedSegment"
 *     [loading]="isDeleting"
 *     (close)="handleClose()"
 *     (confirm)="handleConfirm($event)">
 *   </app-delete-segment-modal>
 *
 * Purpose:
 *   Presents a confirmation modal for removing a segment. Displays segment details (via the provided input)
 *   and provides actions to cancel (close) or confirm deletion. Handles user interaction and emits events
 *   to the parent to perform the actual deletion or to dismiss the modal.
 *
 * Key methods:
 *   - onClose(): void - Emits the `close` event to signal the parent to hide the modal or cancel the action.
 *   - onConfirm(): void - Emits the `confirm` event with the current `segment` payload if available.
 *
 * Lifecycle hooks:
 *   - This component does not implement Angular lifecycle hooks itself; it is driven by inputs and user actions.
 *
 * @input boolean visible - Controls modal visibility; when true the modal is shown.
 * @input Segment | null segment - The segment object targeted for deletion; null when none is selected.
 * @input boolean loading - Indicates an in-flight deletion operation (e.g., to disable controls and show spinner).
 * @output EventEmitter<void> close - Emitted when the user cancels or closes the modal without confirming.
 * @output EventEmitter<Segment> confirm - Emitted when the user confirms deletion; payload is the segment to delete.
 */
@Component({
  selector: 'app-delete-segment-modal',
  standalone: true,
  imports: [CommonModule, MatButtonModule],
  templateUrl: './delete-segment-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./delete-segment-modal.component.scss'],
})
export class DeleteSegmentModalComponent {
  @Input() visible = false;
  @Input() segment: Segment | null = null;
  @Input() loading = false;

  @Output() close = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<Segment>();

  onClose(): void {
    this.close.emit();
  }

  onConfirm(): void {
    if (this.segment) {
      this.confirm.emit(this.segment);
    }
  }
}
